import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { userRequest } from "../api/requestMethods";
import { liveQuoteFor } from "../utils/priceOnRequest";

/**
 * useBuyerQuoteRequests: the buyer's OWN quote requests, for the screens that
 * show a product rather than the quotes list.
 *
 * Founder rule (29 Sep 2026): price on request is an interactive flow, and the
 * progress of that interaction has to be visible until the sale concludes.
 * Until now the only screen that knew any of it was My Quotes, so a buyer who
 * had already asked, and was waiting, came back to the product and met the
 * same "Request a quote" button as someone who had never asked.
 *
 * ONE reader for both storefront surfaces. BrandProductPage asks about a single
 * product and BrandShopPage about a whole catalogue, so without this they would
 * each grow their own fetch, their own idea of which request speaks for a
 * product, and their own words for refusing a second one, which is exactly how
 * the price posture itself came to be decided a dozen different ways before
 * utils/priceOnRequest.js.
 *
 * GET /api/brands/my/quote-requests sits behind `auth`, takes an optional
 * `productId` or `brandId`, and answers newest first with `lifecycleStatus` on
 * every row. The read is skipped entirely while nobody is signed in rather than
 * firing a 401 nobody acts on, and ANY failure is treated as "no request": this
 * decides an affordance, never what may be ordered, so a storefront must never
 * be blocked, emptied or held on a spinner by it.
 *
 * @param {{brandId?: string, productId?: string, enabled?: boolean}} scope
 * @returns {{requests: Array, requestFor: (productId: string) => object|null,
 *            refresh: () => void}}
 */
export default function useBuyerQuoteRequests({
  brandId,
  productId,
  enabled = true,
} = {}) {
  const currentUser = useSelector((state) => state.user?.currentUser);
  const signedIn = Boolean(currentUser);
  const [requests, setRequests] = useState([]);
  /* A counter, not a promise handed back to the caller: a refresh is asked for
     after a successful submit, and the screen that asks should not have to know
     whether the read is still in flight. */
  const [reloadKey, setReloadKey] = useState(0);
  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  /* Narrowed server-side, never here. Reading every request the buyer has ever
     made in order to keep three of them would grow with the buyer's history on
     a screen that cares about one product. */
  const scopeId = productId || brandId || "";
  const scopeKey = productId ? "productId" : "brandId";

  useEffect(() => {
    if (!enabled || !signedIn || !scopeId) {
      setRequests([]);
      return undefined;
    }
    let alive = true;
    userRequest
      .get("/api/brands/my/quote-requests", { params: { [scopeKey]: scopeId } })
      .then((res) => {
        if (alive)
          setRequests(Array.isArray(res?.data?.data) ? res.data.data : []);
      })
      .catch(() => {
        if (alive) setRequests([]);
      });
    return () => {
      alive = false;
    };
  }, [enabled, signedIn, scopeId, scopeKey, reloadKey]);

  /**
   * The request that speaks for one product, or null when the buyer has never
   * asked about it. `liveQuoteFor` prefers a live request over a finished one,
   * so a buyer who asked, was declined, and asked again sees the second
   * conversation rather than the first.
   *
   * The row's `productId` is not populated by this endpoint, so it arrives as
   * an id; it is read through the object case as well because the same rows
   * reach this hook from a populated read elsewhere.
   */
  const requestFor = useCallback(
    (id) => {
      if (!id) return null;
      const forThisProduct = requests.filter((request) => {
        const ref = request?.productId;
        const refId = ref && typeof ref === "object" ? ref._id : ref;
        return refId != null && String(refId) === String(id);
      });
      return liveQuoteFor(forThisProduct);
    },
    [requests],
  );

  return { requests, requestFor, refresh };
}

/**
 * What to say when a buyer asks for a quote they have already asked for.
 *
 * Lives beside the hook because the two always travel together: the hook is
 * what tells a screen that a second request is not wanted, and this is the
 * sentence it then owes the buyer. It names My Quotes as a LINK for the same
 * reason the submit toast does: a next step the shopper cannot take is not a
 * next step.
 *
 * @param {{productName?: string, vendorName?: string}} about
 */
export const duplicateQuoteRequestNotice = ({
  productName,
  vendorName,
} = {}) => (
  <span>
    {`Your request for ${productName || "this product"} is already with ${
      vendorName || "the vendor"
    }. `}
    <Link
      to="/my_profile/my_quotes"
      style={{ color: "inherit", fontWeight: 700, textDecoration: "underline" }}
    >
      See where it has got to in My Quotes
    </Link>
    .
  </span>
);
